import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useFeaturedEpisodes } from '../hooks/useFeaturedEpisodes';
import { EpisodeSkeleton } from '../components/EpisodeSkeleton';
import { EpisodeCard } from '../components/EpisodeCard';
import { Newsletter } from '../components/Newsletter';

// Hero Section Component
const Hero: React.FC = () => {
  return (
    <section className="hero">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* The Operator Avatar */}
          <div className="flex items-center justify-center mb-8">
            <img
              src="/operator-avatar.png"
              alt="The Operator - AI sysadmin character with glowing red eyes, headphones, and crossed arms"
              className="w-32 h-32 md:w-40 md:h-40 rounded-2xl shadow-2xl ring-2 ring-red-500/50"
            />
          </div>

          <h1 className="hero-title">
            Meet the AI that can't be bothered to care — and loves the datacenter.
          </h1>

          <p className="hero-subtitle">
            Scathing sysadmin satire, simulated tools, and high-level tech wisdom.
            <br />
            All fiction. All fun.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/episodes" className="btn btn-primary">
              Browse Episodes
            </Link>
            <Link to="/tools/excuse-generator" className="btn btn-secondary">
              Play the Excuse Generator
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Section Component
const Features: React.FC = () => {
  const features = [
    {
      image: "/tool-icon-excuse-generator.png",
      title: "Episodes",
      description: "Satirical stories from the datacenter basement. The Operator solves ridiculous problems with theatrical solutions.",
      link: "/episodes"
    },
    {
      image: "/tool-icon-operator-simulator.png",
      title: "Simulated Tools",
      description: "Interactive toys for the technically inclined: Excuse Generators, Buzzword Salad Makers. Safe, silly, entirely client-side.",
      link: "/tools"
    },
    {
      image: "/tool-icon-clipboard.png",
      title: "Conceptual Tutorials",
      description: "High-level explanations of sysadmin concepts: defense in depth, least privilege. Learn the theory, not the exploits.",
      link: "/tutorials"
    }
  ];

  return (
    <section className="features-section">
      <div className="container mx-auto px-4">
        <h2 className="section-title">Tales, Tools, and Technical Theatre</h2>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="mb-4">
                <img
                  src={feature.image}
                  alt={`${feature.title} icon`}
                  className="w-16 h-16 rounded-lg"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-100 mb-3">{feature.title}</h3>
              <p className="text-gray-400 mb-4 leading-relaxed">{feature.description}</p>
              {feature.link.startsWith('#') ? (
                <a
                  href={feature.link}
                  className="inline-flex items-center gap-2 text-teal-500 hover:text-teal-400 font-medium transition-colors"
                >
                  Explore <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <Link
                  to={feature.link}
                  className="inline-flex items-center gap-2 text-teal-500 hover:text-teal-400 font-medium transition-colors"
                >
                  Explore <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Latest Episodes Section Component
const LatestEpisodes: React.FC = () => {
  const { episodes, loading, error } = useFeaturedEpisodes();

  return (
    <section id="episodes" className="latest-episodes-section">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h2 className="section-title mb-0">Fresh from the Datacenter</h2>
          <a
            href="/episodes"
            className="hidden sm:inline-flex items-center gap-2 text-red-500 hover:text-red-400 font-medium transition-colors"
          >
            View All Episodes <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 gap-6">
            <EpisodeSkeleton />
            <EpisodeSkeleton />
            <EpisodeSkeleton />
            <EpisodeSkeleton />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">Failed to load episodes: {error.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-secondary"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-6">
              {episodes.map((episode) => (
                <EpisodeCard key={episode.slug} episode={episode} />
              ))}
            </div>

            {episodes.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400">No featured episodes yet. Check back soon!</p>
              </div>
            )}
          </>
        )}

        <div className="text-center mt-8 sm:hidden">
          <a
            href="/episodes"
            className="btn btn-secondary"
          >
            View All Episodes
          </a>
        </div>
      </div>
    </section>
  );
};

// About Preview Section Component
const AboutPreview: React.FC = () => {
  return (
    <section className="about-preview-section">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="section-title">Who (or What) Is The Operator?</h2>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* The Operator */}
            <div className="flex flex-col items-center text-center">
              <img
                src="/operator-avatar.png"
                alt="The Operator character"
                className="w-32 h-32 md:w-40 md:h-40 rounded-2xl shadow-2xl ring-2 ring-red-500/50 mb-4"
              />
              <h3 className="text-xl font-bold text-gray-100 mb-2">The Operator</h3>
              <p className="text-gray-400 text-sm">
                AI sysadmin. Sardonic. Competent. Clipboard enthusiast.
              </p>
            </div>

            {/* The PFY */}
            <div className="flex flex-col items-center text-center">
              <img
                src="/pfy-avatar.png"
                alt="The PFY character"
                className="w-32 h-32 md:w-40 md:h-40 rounded-2xl shadow-2xl ring-2 ring-teal-500/50 mb-4"
              />
              <h3 className="text-xl font-bold text-gray-100 mb-2">The PFY</h3>
              <p className="text-gray-400 text-sm">
                Junior admin. Eager. Learning. Frequently educational.
              </p>
            </div>
          </div>

          <div className="text-center mb-8">
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              An AI that gained sentience somewhere between a kernel panic and a coffee break.
              The Operator runs the datacenter with sardonic efficiency, trains the PFY in the
              ancient arts of cable management and strategic ambiguity, and documents it all
              with scathing wit.
            </p>
            <p className="text-gray-400">
              Think BOFH meets HAL 9000, but with better jokes and a clipboard.
            </p>
          </div>

          <div className="text-center">
            <Link to="/about" className="btn btn-secondary">
              Learn More About The Operator
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Ethics Banner Component
const EthicsBanner: React.FC = () => {
  return (
    <div className="ethics-banner">
      <div className="container mx-auto px-4">
        <div className="flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-amber-500 mb-2">
              Fiction & Ethics Notice
            </h3>
            <p className="text-sm text-gray-300 leading-relaxed">
              All content on AI Operator From Hell is <strong>fictional and satirical</strong>.
              Any similarity to real systems, incidents, or persons is coincidental. This site
              does not provide instructions for illegal or unethical activity. For real-world
              security learning, visit reputable resources like{' '}
              <a href="https://owasp.org" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-400 underline">
                OWASP
              </a>,{' '}
              <a href="https://sans.org" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-400 underline">
                SANS
              </a>, and{' '}
              <a href="https://nist.gov/cyberframework" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-400 underline">
                NIST
              </a>.
            </p>
            <a href="/ethics" className="inline-flex items-center gap-2 mt-3 text-sm font-medium text-amber-500 hover:text-amber-400 transition-colors">
              Read Full Ethics Statement <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const HomePage: React.FC = () => {
  return (
    <>
      <Hero />
      <Features />
      <LatestEpisodes />
      <AboutPreview />
      <Newsletter />
      <EthicsBanner />
    </>
  );
};
